import { createSlice, createSelector } from '@reduxjs/toolkit'
import { selectDashboards } from '../dashboard/dashboardSlice';
import { findTelemetryDashboard, dashboardHasPanels } from '../../utils/dashboardUtils';

const initialApplicationState = {
    modals: [],
    notifications: [],
    uiError: '',
    navigation: {
        login: { name: 'Login', translationKey: 'navigation.login', url: '/login', target: 'in', active: true, now: false },
        profile: { name: 'Perfil', translationKey: 'navigation.profile', url: '/profile', target: 'in', active: true, now: false },
        notifications: { name: 'Notificaciones', translationKey: 'navigation.notifications', url: '/notifications', target: 'in', active: true, now: false },
        dashboard: { name: 'Gráficas', translationKey: 'navigation.dashboard', url: '/dashboard', target: 'in', active: true, now: false },
        telemetry: { name: 'Telemetría', translationKey: 'navigation.telemetry', url: '/telemetry', target: 'in', active: true, now: false },
        admin: { name: 'Admin', translationKey: 'navigation.admin', url: '/admin', target: 'out', active: true },
    }
}

export const applicationSlice = createSlice({
    name: 'application',
    initialState: initialApplicationState,
    reducers: {
        addModal: (state, action) => {
            if (state.modals.find((m) => m.id === action.payload) === undefined)
                state.modals = [...state.modals, { state: false, id: action.payload }]
        },
        openModal: (state, action) => {
            state.modals = state.modals.map((m) => {
                if (m.id === action.payload) {
                    m.state = true
                }
                return m
            })
        },
        closeModal: (state, action) => {
            state.modals = state.modals.map((m) => {
                if (m.id === action.payload) {
                    m.state = false
                }
                return m
            })
        },
        toggleModal: (state, action) => {
            state.modals = state.modals.map((m) => {
                if (m.id === action.payload) {
                    m.state = !m.state
                }
                return m
            })
        },
        pushNotification: (state, action) => {
            state.notifications = [...state.notifications, action.payload]
        },
        removeNotification: (state, action) => {
            state.notifications = [...state.notifications.filter((e) => e.id !== action.payload.id)]
        },
        updateNotification: (state, action) => {
            if (action.payload) {
                state.notifications[state.notifications.findIndex((e) => e.id === action.payload.id)] = { ...action.payload }
            }
        },
        clearNotifications: (state) => {
            state.notifications = []
        },
        setUiError: (state, action) => {
            state.uiError = action.payload;
        },
        setActiveNavItem: (state, action) => {
            const { item } = action.payload;

            state.navigation.login.now = false;
            state.navigation.profile.now = false;
            state.navigation.notifications.now = false;
            state.navigation.dashboard.now = false;
            state.navigation.telemetry.now = false;

            state.navigation[item].now = true;
        },
        setNavItemVisibility: (state, action) => {
            const { item, active } = action.payload;

            state.navigation[item].active = active;
        }
    }
})

export const {
    openModal,
    closeModal,
    toggleModal,
    addModal,
    pushNotification,
    removeNotification,
    updateNotification,
    clearNotifications,
    setUiError,
    setActiveNavItem,
    setNavItemVisibility,
} = applicationSlice.actions

export const selectApplicationStore = (state) => state.application;
export const selectModals = (state) => state.application.modals;
export const selectNotifications = (state) => state.application.notifications;
export const selectUiError = (state) => state.application.uiError;
export const selectNavigation = (state) => state.application.navigation;
export const selectNavigationProfile = (state) => state.application.navigation.profile;
export const selectNavigationNotifications = (state) => state.application.navigation.notifications;
export const selectNavigationDashboard = (state) => state.application.navigation.dashboard;
export const selectNavigationTelemetry = (state) => state.application.navigation.telemetry;
export const selectNavigationAdmin = (state) => state.application.navigation.admin;

// Selectors for icons (profile and notifications)
export const selectNavigationIcons = createSelector(
    [selectNavigationProfile, selectNavigationNotifications],
    (profile, notifications) => ({ profile, notifications })
);

// Selector for written navigation items (dashboard and admin)
export const selectWrittenNavigationItems = createSelector(
    [selectNavigationDashboard, selectNavigationTelemetry, selectNavigationAdmin, selectDashboards],
    (dashboard, telemetry, admin, dashboards) => {
        const items = [];

        const telemetryDashboard = findTelemetryDashboard(dashboards);

        const hasTelemetryPanels = dashboardHasPanels(telemetryDashboard);

        if (dashboard.active) items.push(dashboard);

        if (telemetry.active && hasTelemetryPanels) {
            items.push(telemetry);
        }
        if (admin.active) items.push(admin);
        return items;
    }
);

export const selectActiveNavItem = createSelector(
    [selectNavigation],
    (navigation) => {
        if (navigation.dashboard.now) return navigation.dashboard;
        if (navigation.telemetry.now) return navigation.telemetry;
        if (navigation.profile.now) return navigation.profile;
        if (navigation.notifications.now) return navigation.notifications;
        return null;
    }
);

export const selectModalById = createSelector(
    [selectModals, (state, modalId) => modalId],
    (modals, modalId) => modals.find((m) => m.id === modalId)
);

export const selectIsModalOpen = createSelector(
    [selectModalById],
    (modal) => modal?.state || false
);

export default applicationSlice.reducer
